import type { FoundryV2DomainPacksRecord } from "../api/foundryV2Client";

function statusTone(status: "ready" | "watch" | "blocked"): string {
  if (status === "ready") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "watch") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

type Props = {
  domainPacks: FoundryV2DomainPacksRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundryDomainPacksPanel({ domainPacks, isLoading, error }: Props): JSX.Element {
  const report = domainPacks?.report ?? null;
  const packs = Array.isArray(report?.packs) ? report.packs : [];
  const readyPackIds = Array.isArray(report?.summary.readyPackIds)
    ? report.summary.readyPackIds
    : packs.filter((pack) => pack.status === "ready").map((pack) => pack.id);
  const watchPackIds = Array.isArray(report?.summary.watchPackIds)
    ? report.summary.watchPackIds
    : packs.filter((pack) => pack.status === "watch").map((pack) => pack.id);
  const blockedPackIds = Array.isArray(report?.summary.blockedPackIds)
    ? report.summary.blockedPackIds
    : packs.filter((pack) => pack.status === "blocked").map((pack) => pack.id);
  const globalNextActions = Array.isArray(report?.globalNextActions) ? report.globalNextActions : [];

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#fffdf8_0%,#f6fbff_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-domain-packs-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
            Vertical Domain Packs
          </div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            Finance, industry, health, support, and field ops remain differentiated before Jarvis assembly
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Foundry now compares differentiated vertical packs instead of flattening every generated app into the same
            operating pattern. Each pack exposes KPIs, blockers, policy mappings, flow differences, and visible app
            variation.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !report ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading vertical domain packs...
        </div>
      ) : null}

      {report ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.1fr_0.95fr_0.95fr_0.85fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(report.summary.status)}`}
                >
                  {report.summary.status}
                </span>
                {report.summary.activePackId ? (
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    active {report.summary.activePackId.replace(/_/g, " ")}
                  </span>
                ) : null}
              </div>
              <div className="mt-3 text-sm text-slate-700">{report.summary.headline}</div>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Packs: {report.summary.packCount}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Variation score: {report.summary.variationScore}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Ready: {readyPackIds.length}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Watch/blocked: {watchPackIds.length + blockedPackIds.length}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Vertical truth</div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                {packs.map((pack) => (
                  <span
                    key={pack.id}
                    className={`rounded-full border px-2.5 py-1 font-semibold uppercase tracking-wide ${
                      pack.isActive
                        ? "border-slate-900 bg-slate-900 text-white"
                        : `${statusTone(pack.status)}`
                    }`}
                  >
                    {pack.label}
                  </span>
                ))}
              </div>
              <div className="mt-4 text-xs text-slate-600">
                Active pack, blockers, and differentiated design variants remain visible before generated apps are
                promoted.
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Next actions</div>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                {globalNextActions.map((item) => (
                  <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifact</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={domainPacks?.artifactUrls.domainPacksReport ?? "#"}>
                  domain_packs_report.v1.json
                </a>
              </div>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            {packs.map((pack) => (
              <article key={pack.id} className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="max-w-3xl">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-lg font-semibold text-slate-950">{pack.label}</div>
                      <span
                        className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${statusTone(pack.status)}`}
                      >
                        {pack.status}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        {pack.domainFamily.replace(/_/g, " ")}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        {pack.operatingModel.replace(/_/g, " ")}
                      </span>
                      {pack.isActive ? (
                        <span className="rounded-full border border-slate-900 bg-slate-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                          current project
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2 text-sm text-slate-600">{pack.summary}</div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_1fr]">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">KPIs</div>
                    <div className="mt-3 space-y-2 text-xs text-slate-600">
                      {pack.kpis.map((kpi) => (
                        <div key={kpi.id} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-semibold text-slate-900">{kpi.label}</div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{kpi.target}</div>
                          </div>
                          <div className="mt-1">{kpi.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Generated app variation</div>
                    <div className="mt-3 space-y-2 text-xs text-slate-600">
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                        <div className="font-semibold text-slate-900">
                          {pack.generatedAppVariation.designVariantId ?? "design variant missing"}
                        </div>
                        <div className="mt-1">{pack.generatedAppVariation.uiSignature}</div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                        <div className="font-semibold text-slate-900">Visible attributes</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {pack.generatedAppVariation.visibleAttributes.map((attribute) => (
                            <span
                              key={`${pack.id}-${attribute}`}
                              className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                            >
                              {attribute}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                        <div className="font-semibold text-slate-900">Premium bricks</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {pack.generatedAppVariation.premiumBrickIds.map((brickId) => (
                            <span
                              key={`${pack.id}-premium-${brickId}`}
                              className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                            >
                              {brickId}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_1fr]">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Flow differences</div>
                    <div className="mt-3 space-y-2 text-xs text-slate-600">
                      {pack.flowDifferences.map((flow) => (
                        <div key={flow.id} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-semibold text-slate-900">{flow.label}</div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{flow.lane}</div>
                          </div>
                          <div className="mt-1">{flow.summary}</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {flow.proofPoints.map((proofPoint) => (
                              <span
                                key={`${flow.id}-${proofPoint}`}
                                className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                              >
                                {proofPoint}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Policy mappings & blockers</div>
                    <div className="mt-3 space-y-2 text-xs text-slate-600">
                      {pack.policyMappings.map((mapping) => (
                        <div key={`${pack.id}-${mapping.policyPackId}`} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                          <div className="font-semibold text-slate-900">{mapping.label}</div>
                          <div className="mt-1">{mapping.coverage}</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {mapping.controlIds.map((controlId) => (
                              <span
                                key={`${mapping.policyPackId}-${controlId}`}
                                className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                              >
                                {controlId}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                      {pack.blockers.length ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-red-700">
                          {pack.blockers.join(" ")}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-emerald-700">
                          No active blocker on this pack.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
